import { useRef, useMemo, useCallback } from 'react';
import { RefTransition } from './transition.base.js';
import { cssTransitionSelectors } from './transition.css.selectors.js';
import { jsx } from 'react/jsx-runtime';

/**
 * custom Transtion component for react-transition-group similar to the
 * CSSTransition, however with a slightly altered API to improve DX.
 *
 * After the introduction of ReactStrictMode react-transition-group transitions
 * needs to be provided with a nodeRef prop to not use findDOMNode() which may
 * have a negative impact on performance.
 *
 * However, manually passing refs may become cumbersome to provide. Thus, this
 * transition aims to easy the API required to make Transitions for
 * react-transition-groups by provided all props required for children as
 * arguments.
 */
function CSSRefTransition({
  classNames,
  timeout,
  retainClassnamesOnExit,
  delayMs,
  ...transitionProps
}) {
  const internalRef = useRef(null);
  const nodeRef = transitionProps.nodeRef ?? internalRef;
  const resolvedDelayMs = useMemo(() => delayMs,
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [
  // eslint-disable-next-line react-hooks/exhaustive-deps
  typeof delayMs === "number" ? delayMs : `${delayMs?.enter}-${delayMs?.exit}`]);
  const onEnter = useCallback(isAppearing => {
    nodeRef.current?.classList.add(resolveClassName(classNames, isAppearing ? "appear" : "enter"), isAppearing ? cssTransitionSelectors.isAppearing.className : cssTransitionSelectors.isEntering.className);
    nodeRef.current?.classList.remove(resolveClassName(classNames, "exit"), resolveClassName(classNames, "exit-active"), resolveClassName(classNames, "exit-running"), resolveClassName(classNames, "exit-done"), resolveClassName(classNames, "enter-active"), resolveClassName(classNames, "enter-running"), resolveClassName(classNames, "enter-done"), resolveClassName(classNames, "appear-active"), resolveClassName(classNames, "appear-running"), resolveClassName(classNames, "appear-done"), resolveClassName(classNames, isAppearing ? "enter" : "appear"));
    transitionProps.onEnter?.(isAppearing);
  },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [nodeRef, classNames, transitionProps.onEnter]);
  const onEntering = useCallback(isAppearing => {
    const callback = () => {
      // Ensure that the initial transition values are applied before adding
      // the -active classname
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      nodeRef.current?.offsetHeight;
      nodeRef.current?.classList.remove(resolveClassName(classNames, "exit"), resolveClassName(classNames, "exit-active"), resolveClassName(classNames, "exit-running"), resolveClassName(classNames, "exit-done"), resolveClassName(classNames, "enter-active"), resolveClassName(classNames, "enter-running"), resolveClassName(classNames, "enter-done"), resolveClassName(classNames, "appear-active"), resolveClassName(classNames, "appear-running"), resolveClassName(classNames, "appear-done"), resolveClassName(classNames, isAppearing ? "enter" : "appear"));
      nodeRef.current?.classList.add(resolveClassName(classNames, isAppearing ? "appear" : "enter"), resolveClassName(classNames, isAppearing ? "appear-active" : "enter-active"));
      transitionProps.onEntering?.(isAppearing);
      nodeRef.current?.classList.add(resolveClassName(classNames, isAppearing ? "appear-running" : "enter-running"));
    };
    const enterDelayMs = getDurationValue(delayMs, "enter");
    if (enterDelayMs) {
      setTimeout(callback, enterDelayMs);
    } else {
      requestAnimationFrame(callback);
    }
  },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [nodeRef, classNames, transitionProps.onEntering]);
  const onEntered = useCallback(isAppearing => {
    nodeRef.current?.classList.remove(cssTransitionSelectors.isAppearing.className, cssTransitionSelectors.isEntering.className, resolveClassName(classNames, "exit"), resolveClassName(classNames, "exit-active"), resolveClassName(classNames, "exit-running"), resolveClassName(classNames, "exit-done"), resolveClassName(classNames, "enter"), resolveClassName(classNames, "enter-active"), resolveClassName(classNames, "enter-running"), resolveClassName(classNames, "enter-done"), resolveClassName(classNames, "appear"), resolveClassName(classNames, "appear-active"), resolveClassName(classNames, "appear-running"), resolveClassName(classNames, "appear-done"));
    nodeRef.current?.classList.add(resolveClassName(classNames, isAppearing ? "appear-done" : "enter-done"));
    transitionProps.onEntered?.(isAppearing);
  },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [nodeRef, classNames, transitionProps.onEntered]);
  const onExit = useCallback(() => {
    if (nodeRef.current) {
      nodeRef.current.dataset["disablePolly"] = "true";
    }
    if (!retainClassnamesOnExit) {
      nodeRef.current?.classList.remove(resolveClassName(classNames, "exit-active"), resolveClassName(classNames, "exit-running"), resolveClassName(classNames, "exit-done"), resolveClassName(classNames, "enter"), resolveClassName(classNames, "enter-active"), resolveClassName(classNames, "enter-running"), resolveClassName(classNames, "enter-done"), resolveClassName(classNames, "appear"), resolveClassName(classNames, "appear-active"), resolveClassName(classNames, "appear-running"), resolveClassName(classNames, "appear-done"));
    }
    nodeRef.current?.classList.add(cssTransitionSelectors.isExiting.className, resolveClassName(classNames, "exit"));
    transitionProps.onExit?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodeRef, classNames, transitionProps.onExit]);
  const onExiting = useCallback(() => {
    const callback = () => {
      // Ensure that the initial transition values are applied before adding
      // the -active classname
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      nodeRef.current?.offsetHeight;
      if (!retainClassnamesOnExit) {
        nodeRef.current?.classList.remove(resolveClassName(classNames, "exit-running"), resolveClassName(classNames, "exit-done"), resolveClassName(classNames, "enter"), resolveClassName(classNames, "enter-active"), resolveClassName(classNames, "enter-running"), resolveClassName(classNames, "enter-done"), resolveClassName(classNames, "appear"), resolveClassName(classNames, "appear-active"), resolveClassName(classNames, "appear-running"), resolveClassName(classNames, "appear-done"));
      }
      nodeRef.current?.classList.add(resolveClassName(classNames, "exit"), resolveClassName(classNames, "exit-active"));
      transitionProps.onExiting?.();
      nodeRef.current?.classList.add(resolveClassName(classNames, "exit-running"));
    };
    const exitDelayMs = getDurationValue(resolvedDelayMs, "exit");
    if (exitDelayMs) {
      setTimeout(callback, exitDelayMs);
    } else {
      requestAnimationFrame(callback);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodeRef, classNames, transitionProps.onExiting]);
  const onExited = useCallback(() => {
    nodeRef.current?.classList.remove(cssTransitionSelectors.isExiting.className, resolveClassName(classNames, "exit"), resolveClassName(classNames, "exit-active"), resolveClassName(classNames, "exit-running"), resolveClassName(classNames, "enter"), resolveClassName(classNames, "enter-active"), resolveClassName(classNames, "enter-running"), resolveClassName(classNames, "enter-done"), resolveClassName(classNames, "appear"), resolveClassName(classNames, "appear-active"), resolveClassName(classNames, "appear-running"), resolveClassName(classNames, "appear-done"));
    nodeRef.current?.classList.add(resolveClassName(classNames, "exit-done"));
    if (nodeRef.current) {
      delete nodeRef.current.dataset["disablePolly"];
    }
    transitionProps.onExited?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodeRef, classNames, transitionProps.onExited]);
  const resolvedTimeout = useMemo(() => ({
    appear: getDurationValue(timeout, "appear") + getDurationValue(resolvedDelayMs, "enter"),
    enter: getDurationValue(timeout, "enter") + getDurationValue(resolvedDelayMs, "enter"),
    exit: getDurationValue(timeout, "exit") + getDurationValue(resolvedDelayMs, "exit")
  }), [resolvedDelayMs, timeout]);
  return /*#__PURE__*/jsx(RefTransition, {
    ...transitionProps,
    timeout: resolvedTimeout,
    onEnter: onEnter,
    onEntering: onEntering,
    onEntered: onEntered,
    onExit: onExit,
    onExiting: onExiting,
    onExited: onExited,
    nodeRef: nodeRef,
    children: transitionProps.children
  });
}
function resolveClassName(base, stage) {
  let resolvedBase = base;
  if (typeof base === "object") {
    switch (stage) {
      case "appear":
        resolvedBase = base.appear;
        break;
      case "appear-active":
        resolvedBase = base.appearActive;
        break;
      case "appear-running":
        resolvedBase = base.appearRunning;
        break;
      case "appear-done":
        resolvedBase = base.appearDone;
        break;
      case "enter":
        resolvedBase = base.enter;
        break;
      case "enter-active":
        resolvedBase = base.enterActive;
        break;
      case "enter-running":
        resolvedBase = base.enterRunning;
        break;
      case "enter-done":
        resolvedBase = base.enterDone;
        break;
      case "exit":
        resolvedBase = base.exit;
        break;
      case "exit-active":
        resolvedBase = base.exitActive;
        break;
      case "exit-running":
        resolvedBase = base.exitRunning;
        break;
      case "exit-done":
        resolvedBase = base.exitDone;
        break;
    }
  }
  if (resolvedBase) {
    return `${resolvedBase}-${stage}`;
  } else {
    return stage;
  }
}
function getDurationValue(duration, key) {
  return typeof duration === "number" ? duration : duration?.[key] ?? 0;
}

export { CSSRefTransition, getDurationValue };
//# sourceMappingURL=transition.css.js.map
